import React from 'react';
import { useWidgetDataLoader } from './WidgetDataLoader';
import { WidgetRenderer } from '../common';
import {
  isDirectFieldReference,
  containsFieldReference,
  extractDirectFieldValue,
  replaceFieldReferences,
  isExpressionTemplate,
} from './utils/jsonPathUtils';
import { cn } from '../../../lib/utils';
import { evaluateExpression } from './utils/expression-engine';

import { useTranslation } from 'react-i18next';
import i18n from 'i18next';

// Error boundary component for widget
class WidgetErrorBoundary extends React.Component<
  { children: React.ReactNode; className?: string },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode; className?: string }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Widget error boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      const message = this.state.error?.message;
      return (
        <div className={this.props.className}>
          <div className="flex h-full items-center justify-center text-gray-500 dark:text-gray-400">
            <div className="text-center">
              <div className="text-lg font-medium">
                {i18n.t('errorLoadingWidget', { ns: 'pleri/dashboard' })}
              </div>
              <div className="text-sm">
                {i18n.t('pleaseTryRefreshingThePage', {
                  ns: 'pleri/dashboard',
                })}
              </div>
              {message && (
                <div className="mt-2 max-w-md text-xs break-words text-red-600 dark:text-red-400">
                  {message}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Types for widget configuration
export interface DataSource {
  type: 'tool';
  toolName: string;
  parameters: {
    intention: string;
    [key: string]: unknown;
  };
  // JSONPath-based approach - reference filter values directly
  filterParameterPaths?: Record<string, unknown>; // parameterName -> JSONPath expression or structure
}

export interface GridLayout {
  cols?: number; // Number of columns (1-12)
  gap?: number; // Gap size (1-8)
  colSpan?: number; // How many columns to span
  rowSpan?: number; // How many rows to span
  colStart?: number; // Starting column (1-12)
  colEnd?: number; // Ending column (1-13)
  rowStart?: number; // Starting row
  rowEnd?: number; // Ending row
}

export interface WidgetConfig {
  component:
    | 'MetricCard'
    | 'DataTable'
    | 'Chart'
    | 'StatusCard'
    | 'ProgressCard'
    | 'CategoryCard';
  props: {
    [key: string]: unknown;
  };
  dataSources?: DataSource[];
  layout?: {
    className?: string;
    grid?: GridLayout; // Grid positioning
  };
  // dashboardId is now provided by context, no longer needed in config
  source?: string;
}

export interface WidgetProps {
  config: WidgetConfig;
  className?: string;
  isStreaming?: boolean;
}

// Utility function to resolve prop values (static or dynamic)
const resolvePropValue = (
  propValue: unknown,
  dataResults: Record<string, unknown>,
): unknown => {
  // Handle arrays recursively
  if (Array.isArray(propValue)) {
    return propValue.map((item) => resolvePropValue(item, dataResults));
  }

  // Handle objects recursively
  if (propValue && typeof propValue === 'object' && !Array.isArray(propValue)) {
    const resolvedObj: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(propValue)) {
      const resolvedValue = resolvePropValue(value, dataResults);
      if (resolvedValue !== undefined) {
        resolvedObj[key] = resolvedValue;
      }
    }
    return resolvedObj;
  }

  // Handle string values (existing logic)
  if (typeof propValue === 'string') {
    if (isExpressionTemplate(propValue)) {
      return evaluateExpression(propValue, {
        data: {
          ...dataResults,
          ...(dataResults['source_0'] as Record<string, unknown>),
        },
      });
    }
    // Check if it's a direct field reference
    if (isDirectFieldReference(propValue)) {
      return extractDirectFieldValue(propValue, dataResults);
    }

    // Check if it contains field references (template strings)
    if (containsFieldReference(propValue)) {
      return replaceFieldReferences(propValue, dataResults);
    }
  }

  // Static value (including functions)
  return propValue;
};

const WidgetContent: React.FC<WidgetProps> = ({
  config,
  className,
  isStreaming = false,
}) => {
  const { t } = useTranslation('pleri/dashboard');
  const { dataResults, isLoading, error } = useWidgetDataLoader(
    config.dataSources,
  );

  // Resolve all props based on static values and dynamic data
  const resolvedProps = React.useMemo(() => {
    const props: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(config.props || {})) {
      const resolvedValue = resolvePropValue(value, dataResults);
      if (resolvedValue !== undefined) {
        props[key] = resolvedValue;
      }
      // If resolvedValue is undefined, the prop is skipped
      // This allows widgets to handle missing data gracefully
    }

    return props;
  }, [config.props, dataResults]);

  // Handle loading state
  if (isLoading) {
    return (
      <div className={cn(config.layout?.className || className, 'h-full')}>
        <div className="flex h-full items-center justify-center text-gray-500 dark:text-gray-400">
          <div className="text-center">
            <div className="text-lg font-medium">{t('common:loading')}</div>
            <div className="text-sm">{t('pleaseWait')}</div>
          </div>
        </div>
      </div>
    );
  }

  // Handle error state - show loading if streaming, otherwise show error
  if (error) {
    if (isStreaming) {
      return (
        <div className={cn(config.layout?.className || className, 'h-full')}>
          <div className="flex h-full items-center justify-center">
            <div className="w-full animate-pulse p-6">
              <div className="mb-2 h-4 w-24 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div className="mb-2 h-8 w-32 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div className="h-3 w-20 rounded bg-gray-200 dark:bg-gray-700"></div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={cn(config.layout?.className || className, 'h-full')}>
        <div className="flex h-full items-center justify-center text-gray-500 dark:text-gray-400">
          <div className="text-center">
            <div className="text-lg font-medium">{t('errorLoadingData')}</div>
            <div className="text-sm">{t('common:pleaseTryAgainLater')}</div>
          </div>
        </div>
      </div>
    );
  }

  // Render the appropriate component based on config
  return (
    <WidgetRenderer
      componentType={config.component}
      props={resolvedProps}
      className="h-full"
      layoutClassName={config.layout?.className || className}
      showChat={true}
      componentName={config.component}
      componentData={resolvedProps}
      chatPosition="top-right"
      config={config}
      isStreaming={isStreaming}
    />
  );
};

export const Widget: React.FC<WidgetProps> = ({
  config,
  className,
  isStreaming = false,
}) => {
  return (
    <WidgetErrorBoundary
      className={cn(config.layout?.className || className, 'h-full')}
      key={JSON.stringify(config)}
    >
      <WidgetContent
        config={config}
        className={className}
        isStreaming={isStreaming}
      />
    </WidgetErrorBoundary>
  );
};
